import { useEffect, useState } from "react";

const SAMPLE_INTERVAL_MS = 55;

/**
 * Live microphone loudness as a rolling history (oldest first, 0..1), read
 * from an AnalyserNode on the recording stream, so the bars follow the voice
 * instead of a canned animation. Flat while there is no stream. Same approach
 * as TogoAiLab's recorder.
 */
export function useMicLevels(stream: MediaStream | null, count: number): number[] {
  const [levels, setLevels] = useState<number[]>(() => new Array(count).fill(0));

  useEffect(() => {
    if (!stream) {
      setLevels(new Array(count).fill(0));
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const context = new Ctor();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);

    const timer = window.setInterval(() => {
      analyser.getByteTimeDomainData(samples);
      setLevels((previous) => [...previous.slice(1), levelFromSamples(samples)]);
    }, SAMPLE_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
      source.disconnect();
      void context.close();
    };
  }, [stream, count]);

  return levels;
}

/** RMS of 8-bit time-domain samples, stretched so normal speech fills the bars. */
export function levelFromSamples(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const centered = (samples[i] - 128) / 128;
    sum += centered * centered;
  }
  return Math.min(1, Math.sqrt(sum / samples.length) * 4);
}
